import { Fragment, useEffect, useState } from 'react'
import { AddEntrenamientoModal } from './AddEntrenamientoModal'
import { useLang } from '../i18n/LangProvider'
import { useAsistenciaApi, ApiError } from './api'
import { t, mesLabel, diaSemanaLabel, Lang } from './i18n'
import { mesesTemporada } from './temporadaMeses'
import { NotaOverlay } from './NotaOverlay'
import { AnadirMiembroBotones, Grupo } from './roles'
import { FichaOverlay } from './FichaOverlay'
import { EstadoDiaMenu } from './EstadoDiaMenu'
import styles from './AsistenciaTab.module.css'
import { pctPillClass } from '../styles/thresholds'
import { useIsMobile } from '../styles/useIsMobile'
import { useLongPress } from './useLongPress'
import { useScrollActiveIntoView } from '../styles/useScrollActiveIntoView'
import { DiaHeader } from './DiaHeader'

interface Sesion {
  id: string
  fecha: string
  numero: number | null
  tipo: string
  origen: string
  // Feedback del club por vídeo, 2026-10-11: "% = presencias / sesiones celebradas... revisa el
  // denominador de SESIÓN %". El backend ya calcula este % (mismo criterio de elegibilidad y de
  // "solo sesiones celebradas" que el resto de la pantalla) — `null` si la sesión aún no se ha
  // celebrado o no hay ningún jugador elegible ese día.
  porcentaje: number | null
}

// 'X' = sesión eliminada solo para esta persona (no cuenta en su %, ni como falta).
// 'EN' / 'LS' / 'JU' / 'NJ' = falta por enfermedad / lesión / justificada / sin motivo (cuentan como falta).
type Estado = 'P' | 'A' | 'X' | 'EN' | 'LS' | 'JU' | 'NJ' | null

const FALTA_ICONO: Record<string, string> = { EN: '🤒', LS: '🩹', JU: 'J', NJ: '?' }

interface Registro {
  sesion_id: string
  estado: Estado
  nota: string | null
}

interface Miembro {
  id: string
  grupo: string
  rol_entrenador: string | null
  orden: number
  persona: { nombre: string; alias: string | null; foto_url: string | null }
  registros: Registro[]
  porcentaje_mes: number | null
  porcentaje_ano: number | null
}

interface AsistenciaResponse {
  sesiones: Sesion[]
  miembros: Miembro[]
}

// Feedback del club, 2026-10-05: "deberían verse todos igual que en f11... tanto eskola, como f7 y
// f11 deberían estar igual" — misma estructura de secciones ya usada en F11 (issue #46: numeración
// continua a través de los tres grupos, sin reiniciar en cada uno).
const GRUPOS: { key: string; labelKey: 'conFicha' | 'sinFicha' | 'entrenadores' }[] = [
  { key: 'con_ficha', labelKey: 'conFicha' },
  { key: 'sin_ficha', labelKey: 'sinFicha' },
  { key: 'entrenador', labelKey: 'entrenadores' },
]

function fechaCorta(fechaISO: string): string {
  return fechaISO.slice(0, 10)
}

function diaNumero(fechaISO: string): number {
  return Number(fechaISO.slice(8, 10))
}

function mesActualISO(): string {
  const now = new Date()
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`
}

function nextCiclo(estado: Estado): Estado {
  // Issue #5: un clic marca (✓), el siguiente vacía la casilla; una "A" ya guardada también se vacía.
  // Una sesión eliminada para esta persona ('X') o una falta con motivo vuelve a contar como
  // presente al pulsarla; una presencia (o una "A" antigua) se vacía.
  return estado === 'P' || estado === 'A' ? null : 'P'
}

function PctPill({ pct }: { pct: number | null }) {
  return <span className={pctPillClass(pct, 80, 60)}>{pct == null ? '--' : `${pct}%`}</span>
}

export function AsistenciaTab({
  equipoId,
  // Issue #41, 2026-10-02: F7 se marca desde septiembre, UKE Eskola desde octubre (antes:
  // enero-diciembre para ambas, issue #33). Por defecto 'f7' para no romper quien no lo pase.
  categoria = 'f7',
  lang: langProp,
  readOnly = false,
  // Issue #65, 2026-10-09: "en UKE Eskola y F7, solo el administrador puede hacer esto" — habilita
  // el botón de borrar la sesión completa de un jugador en el panel de notas.
  esAdmin = false,
}: {
  equipoId: string
  categoria?: 'f7' | 'eskola'
  lang?: Lang
  readOnly?: boolean
  esAdmin?: boolean
}) {
  const ctxLang = useLang()
  const lang = langProp ?? ctxLang
  const api = useAsistenciaApi()
  const [mes, setMes] = useState(mesActualISO())
  const [anadiendoEntrenamiento, setAnadiendoEntrenamiento] = useState(false)
  const [data, setData] = useState<AsistenciaResponse | null>(null)
  const [editMode, setEditMode] = useState(false)
  const [notaCelda, setNotaCelda] = useState<{ sesion: Sesion; miembro: Miembro } | null>(null)
  const [fichaMiembroId, setFichaMiembroId] = useState<string | null>(null)
  const [error, setError] = useState<'forbidden' | 'other' | null>(null)
  const [dragId, setDragId] = useState<string | null>(null)
  const [estadoDiaMenu, setEstadoDiaMenu] = useState<{ x: number; y: number; sesion: Sesion } | null>(null)
  // fix-mobile-responsive (design D1/D2): en móvil solo la columna del nombre es fija y los % van
  // dentro de ella — con 4 columnas fijas no quedaba sitio para ver ninguna sesión. `leadCols` es el
  // número de columnas antes de las sesiones, del que dependen los `colSpan` de las filas resumen.
  const isMobile = useIsMobile()
  const leadCols = isMobile ? 1 : 4
  const longPress = useLongPress()
  const mesesRef = useScrollActiveIntoView<HTMLElement>('button:disabled', [mes, data])

  const meses = mesesTemporada(categoria)

  async function refresh() {
    try {
      const res: AsistenciaResponse = await api.get(`/equipos/${equipoId}/asistencia?mes=${mes}`)
      setData(res)
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError && err.status === 403 ? 'forbidden' : 'other')
    }
  }

  async function handleWriteAction(action: () => Promise<void>) {
    try {
      await action()
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        window.alert(t('temporadaCerrada', lang))
      } else {
        window.alert(t('errorGenerico', lang))
      }
    }
  }

  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mes, equipoId])

  async function handleCellClick(sesion: Sesion, miembro: Miembro, actual: Estado) {
    if (readOnly) return
    const nuevo = nextCiclo(actual)
    await handleWriteAction(async () => {
      await api.patch(`/equipos/${equipoId}/asistencia`, {
        sesion_id: sesion.id,
        miembro_equipo_id: miembro.id,
        estado: nuevo,
      })
      await refresh()
    })
  }

  // Click derecho (escritorio) o pulsación larga (táctil) — ver `useLongPress`.
  function handleCellNota(sesion: Sesion, miembro: Miembro) {
    setNotaCelda({ sesion, miembro })
  }

  async function handleSaveNota(nota: string) {
    if (!notaCelda) return
    const registro = notaCelda.miembro.registros.find((r) => r.sesion_id === notaCelda.sesion.id)
    await handleWriteAction(async () => {
      await api.patch(`/equipos/${equipoId}/asistencia`, {
        sesion_id: notaCelda.sesion.id,
        miembro_equipo_id: notaCelda.miembro.id,
        estado: registro?.estado ?? null,
        nota,
      })
      setNotaCelda(null)
      await refresh()
    })
  }

  async function handleMarcarFalta(estado: 'EN' | 'LS' | 'JU' | 'NJ') {
    if (!notaCelda) return
    const registro = notaCelda.miembro.registros.find((r) => r.sesion_id === notaCelda.sesion.id)
    await handleWriteAction(async () => {
      await api.patch(`/equipos/${equipoId}/asistencia`, {
        sesion_id: notaCelda.sesion.id,
        miembro_equipo_id: notaCelda.miembro.id,
        // Pulsar el motivo ya marcado lo quita (vuelve a celda vacía, que también es falta).
        estado: registro?.estado === estado ? null : estado,
        nota: registro?.nota ?? undefined,
      })
      setNotaCelda(null)
      await refresh()
    })
  }

  async function handleDeleteNota() {
    if (!notaCelda) return
    const registro = notaCelda.miembro.registros.find((r) => r.sesion_id === notaCelda.sesion.id)
    await handleWriteAction(async () => {
      await api.patch(`/equipos/${equipoId}/asistencia`, {
        sesion_id: notaCelda.sesion.id,
        miembro_equipo_id: notaCelda.miembro.id,
        estado: registro?.estado ?? null,
        nota: '',
      })
      setNotaCelda(null)
      await refresh()
    })
  }

  // Issue #65, 2026-10-09: a diferencia de `handleDeleteNota` (borra solo el texto de la nota),
  // esto quita la sesión a ESE jugador (marca + nota). El resto de jugadores y la sesión en sí no se
  // tocan. Feedback del club por vídeo, 2026-10-12 ("no se elimina, no cambia el porcentaje"):
  // antes se vaciaba la celda (`estado: null`), pero una sesión celebrada sin marcar cuenta como
  // falta en Eskola/F7, así que el % no cambiaba. Ahora se guarda `X`, que el backend excluye del
  // numerador y del denominador.
  async function handleDeleteRegistro() {
    if (!notaCelda) return
    await handleWriteAction(async () => {
      await api.patch(`/equipos/${equipoId}/asistencia`, {
        sesion_id: notaCelda.sesion.id,
        miembro_equipo_id: notaCelda.miembro.id,
        estado: 'X',
        nota: '',
      })
      setNotaCelda(null)
      await refresh()
    })
  }

  async function handleQuitarSesion(sesionId: string) {
    if (!window.confirm(t('quitarDiaConfirm', lang))) return
    await handleWriteAction(async () => {
      await api.patch(`/equipos/${equipoId}/sesiones/${sesionId}`, { eliminada: true })
      await refresh()
    })
  }

  // Issue #52, 2026-10-02: "en F7 y UKE Eskola, añade una mini pestañita desplegable... Todos
  // asisten, No hay entrenamiento (festivo), Se suspende el entrenamiento" — mismo mecanismo que ya
  // tiene F11 desde el issue #32/#45, con el mismo componente compartido (`EstadoDiaMenu`).
  async function marcarTodos(sesion: Sesion) {
    const sinMarcar = data!.miembros.filter((m) => !m.registros.find((r) => r.sesion_id === sesion.id)?.estado)
    await handleWriteAction(async () => {
      await Promise.all(
        sinMarcar.map((m) =>
          api.patch(`/equipos/${equipoId}/asistencia`, {
            sesion_id: sesion.id,
            miembro_equipo_id: m.id,
            estado: 'P',
          }),
        ),
      )
      await refresh()
    })
  }

  async function handleEliminarJugador(miembroId: string) {
    if (!window.confirm(t('eliminarJugadorConfirm', lang))) return
    await handleWriteAction(async () => {
      await api.patch(`/equipos/${equipoId}/miembros/${miembroId}`, {
        fecha_baja: new Date().toISOString().slice(0, 10),
      })
      await refresh()
    })
  }

  // Issue #51 "orden jugadores", 2026-10-02: permite corregir desde la propia pestaña Asistencia el
  // orden de un equipo que ya se importó con el bug de `orden` siempre a 0 (arreglado esta mañana
  // solo para altas nuevas) — mismo mecanismo de arrastrar-y-soltar ya existente en Plantilla y F11.
  // Feedback del club, 2026-10-05: el reordenado ahora se acota al grupo (con/sin ficha/entrenador)
  // de la fila arrastrada, igual que F11, ya que la tabla se organiza en secciones por grupo.
  async function handleDrop(grupo: string, targetId: string) {
    if (!dragId || dragId === targetId || !data) return
    const miembrosGrupo = data.miembros.filter((m) => m.grupo === grupo).sort((a, b) => a.orden - b.orden)
    const fromIndex = miembrosGrupo.findIndex((m) => m.id === dragId)
    const toIndex = miembrosGrupo.findIndex((m) => m.id === targetId)
    if (fromIndex === -1 || toIndex === -1) return
    const reordenados = [...miembrosGrupo]
    const [moved] = reordenados.splice(fromIndex, 1)
    reordenados.splice(toIndex, 0, moved)

    await handleWriteAction(async () => {
      await Promise.all(reordenados.map((m, index) => api.patch(`/equipos/${equipoId}/miembros/${m.id}`, { orden: index })))
      await refresh()
    })
    setDragId(null)
  }

  async function handleAddMiembro(nombre: string, grupo: Grupo) {
    await handleWriteAction(async () => {
      await api.post(`/equipos/${equipoId}/miembros`, { nombre, grupo })
      await refresh()
    })
  }

  // Issue #39: reactiva un día festivo/suspendido o añade un entrenamiento extraordinario; el
  // backend decide internamente si crea la sesión o reactiva una ya existente marcada eliminada.
  // 2026-10-23: la fecha llega del diálogo `AddEntrenamientoModal` (antes `window.prompt`, que no
  // funciona en todos los navegadores); el error del API se devuelve para mostrarlo en el diálogo.
  async function handleAnadirEntrenamiento(fecha: string): Promise<string | null> {
    let error: string | null = null
    await handleWriteAction(async () => {
      try {
        await api.post(`/equipos/${equipoId}/sesiones`, { tipo: 'entrenamiento', fecha })
      } catch (err) {
        // Fecha fuera de temporada: el API explica qué falla.
        if (err instanceof ApiError && err.status === 400) {
          error = err.message
          return
        }
        throw err
      }
      await refresh()
    })
    return error
  }

  async function handleExportar() {
    await handleWriteAction(async () => {
      const { blob, filename } = await api.downloadFile(`/equipos/${equipoId}/asistencia/exportar?mes=${mes}`)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
    })
  }

  if (error === 'forbidden') {
    return (
      <div>
        <h3>{t('sinAcceso', lang)}</h3>
        <p>{t('sinAccesoAsistenciaMensaje', lang)}</p>
      </div>
    )
  }
  if (error === 'other') {
    return <p>{t('errorGenerico', lang)}</p>
  }
  if (!data) return <p>{t('sinDatos', lang)}</p>

  const sesiones = data.sesiones
  const totalSesiones = sesiones.length
  // Issue #84: los entrenadores ya salen en la tabla, pero las cifras del resumen siguen siendo de
  // los jugadores (como hasta ahora).
  const jugadores = data.miembros.filter((m) => m.grupo === 'con_ficha' || m.grupo === 'sin_ficha')
  const conFichaCount = data.miembros.filter((m) => m.grupo === 'con_ficha').length
  const sinFichaCount = data.miembros.filter((m) => m.grupo === 'sin_ficha').length
  const asistenciasTotal = jugadores.reduce((acc, m) => acc + m.registros.filter((r) => r.estado === 'P').length, 0)
  const mediaGeneral = jugadores.length
    ? Math.round((jugadores.reduce((acc, m) => acc + (m.porcentaje_mes ?? 0), 0) / jugadores.length) * 10) / 10
    : 0
  const mediaFicha = conFichaCount
    ? Math.round(
        (data.miembros.filter((m) => m.grupo === 'con_ficha').reduce((acc, m) => acc + (m.porcentaje_mes ?? 0), 0) /
          conFichaCount) *
          10,
      ) / 10
    : 0

  return (
    <div className={styles.layout}>
      <aside className={styles.sidebar} ref={mesesRef}>
        {meses.map((m) => (
          <button key={m} onClick={() => setMes(m)} disabled={m === mes}>
            {mesLabel(m, lang)}
          </button>
        ))}
      </aside>

      <div>
        <h3>{mesLabel(mes, lang)}</h3>
        <div className={styles.toolbar}>
          {!readOnly && <AnadirMiembroBotones lang={lang} onAdd={handleAddMiembro} />}
          {!readOnly && <button onClick={() => setAnadiendoEntrenamiento(true)}>{t('anadirEntrenamiento', lang)}</button>}
          {anadiendoEntrenamiento && (
            <AddEntrenamientoModal
              lang={lang}
              onAdd={handleAnadirEntrenamiento}
              onClose={() => setAnadiendoEntrenamiento(false)}
            />
          )}
          <button onClick={() => setEditMode((v) => !v)}>{editMode ? t('guardarDias', lang) : t('editarDias', lang)}</button>
          <button onClick={handleExportar}>{t('exportarCsv', lang)}</button>
        </div>

        <dl className="stat-card-grid">
          <div className="stat-card">
            <dt>{t('sesiones', lang)}</dt>
            <dd>{totalSesiones}</dd>
          </div>
          <div className="stat-card">
            <dt>{t('totalTemporada', lang)}</dt>
            <dd>{totalSesiones}</dd>
          </div>
          <div className="stat-card">
            <dt>{t('conFicha', lang)}</dt>
            <dd>{conFichaCount}</dd>
          </div>
          <div className="stat-card">
            <dt>{t('sinFicha', lang)}</dt>
            <dd>{sinFichaCount}</dd>
          </div>
          <div className="stat-card">
            <dt>{t('asistencias', lang)}</dt>
            <dd>{asistenciasTotal}</dd>
          </div>
          <div className="stat-card">
            <dt>{t('mediaGeneral', lang)}</dt>
            <dd>{mediaGeneral}%</dd>
          </div>
          <div className="stat-card">
            <dt>{t('mediaFicha', lang)}</dt>
            <dd>{mediaFicha}%</dd>
          </div>
        </dl>

        <p>{t('leyendaMarcado', lang)}</p>

        {/* Look & feel (club, 2026-10-23): "tenemos fijada la columna de jugadores, pero no la cabecera
            de las jornadas" — `sticky-head` (App.css) fija también la fila de fechas. */}
        <div className={`${styles.tableWrap} sticky-head`}>
        <table className={`${styles.table} ${isMobile ? styles.mobile : ''}`}>
          <thead>
            <tr>
              {!isMobile && (
                <>
                  <th>#</th>
                  <th>{t('porcMes', lang)}</th>
                  <th>{t('porcAno', lang)}</th>
                </>
              )}
              <th>{t('jugador', lang)}</th>
              {data.sesiones.map((sesion) => (
                <th key={sesion.id}>
                  {/* Issue #52, 2026-10-02: mismo menú compacto "estado del día" ya usado por F11
                      (Todos asisten / Festivo / Suspendido), en vez del botón ✕ suelto. Desde el
                      2026-10-23 se abre pulsando la propia cabecera (ver `DiaHeader`). */}
                  <DiaHeader
                    semana={diaSemanaLabel(fechaCorta(sesion.fecha), lang)}
                    dia={diaNumero(sesion.fecha)}
                    onOpen={readOnly ? undefined : (e) => setEstadoDiaMenu({ x: e.clientX, y: e.clientY, sesion })}
                  />
                </th>
              ))}
              <th>TOT</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              {!isMobile && <td colSpan={3} />}
              <td>{t('sesionPorc', lang)}</td>
              {data.sesiones.map((sesion) => {
                // Issue real del club (feedback por vídeo, 2026-10-11): este % se calculaba aquí
                // mismo con un denominador roto — `data.miembros.filter(m => m.registros.some(...))`
                // es siempre verdadero para todos los jugadores (el array `registros` siempre trae
                // una entrada por sesión, marcada o no), así que el denominador era simplemente
                // "todos los jugadores de la página", sin comprobar si estaban de baja en la fecha
                // de esa sesión. Ahora se usa el % que ya calcula el backend, con el mismo criterio
                // de elegibilidad y "solo sesiones celebradas" que el resto de la pantalla.
                const pct = sesion.porcentaje == null ? '--' : `${sesion.porcentaje}%`
                return <td key={sesion.id}>{pct}</td>
              })}
              <td />
            </tr>

            {(() => {
              // Issue #46, 2026-10-02: numeración correlativa 1..N a lo largo de todo el equipo,
              // continuando a través de los tres grupos (Con Ficha / Sin Ficha / Entrenadores), no
              // reiniciando en cada uno.
              let numeroGlobal = 0
              return GRUPOS.map(({ key, labelKey }) => {
                const miembrosGrupo = data.miembros.filter((m) => m.grupo === key).sort((a, b) => a.orden - b.orden)
                if (miembrosGrupo.length === 0) return null

                const totalGrupo = miembrosGrupo.reduce((acc, m) => acc + m.registros.filter((r) => r.estado === 'P').length, 0)
                const mediaGrupo = miembrosGrupo.length
                  ? Math.round((miembrosGrupo.reduce((acc, m) => acc + (m.porcentaje_mes ?? 0), 0) / miembrosGrupo.length) * 10) / 10
                  : 0

                return (
                  <Fragment key={key}>
                    <tr key={`${key}-header`}>
                      <td colSpan={leadCols + sesiones.length + 1}>
                        <strong>
                          {t(labelKey, lang)} ({miembrosGrupo.length})
                        </strong>
                      </td>
                    </tr>
                    {miembrosGrupo.map((miembro) => {
                      numeroGlobal += 1
                      return (
                        <tr
                          key={miembro.id}
                          draggable
                          onDragStart={() => setDragId(miembro.id)}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={() => handleDrop(key, miembro.id)}
                        >
                          {!isMobile && (
                            <>
                              <td>{numeroGlobal}</td>
                              <td>
                                <PctPill pct={miembro.porcentaje_mes} />
                              </td>
                              <td>
                                <PctPill pct={miembro.porcentaje_ano} />
                              </td>
                            </>
                          )}
                          {/* Safari: con `display: flex` en el propio <td> deja de ser celda de tabla y el `sticky`
                              queda confinado en una celda anónima — no se fija. El flex va en un div interior. */}
                          <td className={styles.playerTd}>
                            <div className={styles.playerCell}>
                              <span className={styles.playerAvatar}>
                                {miembro.persona.foto_url ? (
                                  <img src={miembro.persona.foto_url} alt="" width={32} height={32} />
                                ) : (
                                  <span aria-hidden>
                                    {miembro.persona.nombre
                                      .split(' ')
                                      .slice(0, 2)
                                      .map((p) => p[0]?.toUpperCase())
                                      .join('')}
                                  </span>
                                )}
                              </span>
                              {/* Issue #60, 2026-10-06: "que se vea siempre el nombre completo... si
                                  no cabe, nombre arriba y apellido debajo", en vez de cortar con "…"
                                  — texto normal, wrap natural en el espacio (ver `.playerName`). */}
                              <span className={styles.playerInfo}>
                                <button className={styles.playerName} onClick={() => setFichaMiembroId(miembro.id)}>
                                  {isMobile && <span className={styles.numero}>{numeroGlobal}.</span>}
                                  {miembro.persona.nombre}
                                </button>
                                {isMobile && (
                                  <span className={styles.mobilePcts}>
                                    <span>
                                      {t('porcMes', lang)} <PctPill pct={miembro.porcentaje_mes} />
                                    </span>
                                    <span>
                                      {t('porcAno', lang)} <PctPill pct={miembro.porcentaje_ano} />
                                    </span>
                                  </span>
                                )}
                              </span>
                              {editMode && (
                                <button onClick={() => handleEliminarJugador(miembro.id)} aria-label="eliminar-jugador">
                                  ✕
                                </button>
                              )}
                            </div>
                          </td>
                          {data.sesiones.map((sesion) => {
                            const registro = miembro.registros.find((r) => r.sesion_id === sesion.id)
                            if (!registro) return <td key={sesion.id} />
                            const cellClass =
                              registro.estado === 'P'
                        ? 'cell-p'
                        : registro.estado === 'A'
                          ? 'cell-a'
                          : registro.estado === 'X'
                            ? 'cell-excluida'
                            : registro.estado && FALTA_ICONO[registro.estado]
                              ? `cell-falta-${registro.estado.toLowerCase()}`
                              : 'cell-empty'
                            return (
                              <td
                                key={sesion.id}
                                className={`${styles.cell} ${cellClass}`}
                                {...longPress(
                                  () => handleCellNota(sesion, miembro),
                                  () => handleCellClick(sesion, miembro, registro.estado),
                                )}
                              >
                                {registro.estado === 'P'
                                  ? '✓'
                                  : registro.estado === 'A'
                                    ? '✗'
                                    : registro.estado === 'X'
                                      ? '—'
                                      : (FALTA_ICONO[registro.estado ?? ''] ?? '·')}
                                {registro.nota && <span aria-label="tiene-nota">•</span>}
                              </td>
                            )
                          })}
                          <td>{miembro.registros.filter((r) => r.estado === 'P').length}</td>
                        </tr>
                      )
                    })}
                    <tr key={`${key}-total`}>
                      <td colSpan={leadCols}>{t('total', lang)}</td>
                      <td colSpan={sesiones.length} />
                      <td>{totalGrupo}</td>
                    </tr>
                    <tr key={`${key}-media`}>
                      <td colSpan={leadCols}>{t('media', lang)}</td>
                      <td colSpan={sesiones.length} />
                      <td>{mediaGrupo}%</td>
                    </tr>
                  </Fragment>
                )
              })
            })()}

            <tr>
              <td colSpan={leadCols}>
                <strong>{t('totalGeneral', lang)}</strong>
              </td>
              <td colSpan={sesiones.length} />
              <td>{asistenciasTotal}</td>
            </tr>
          </tbody>
        </table>
        </div>
      </div>

      {estadoDiaMenu && (
        <EstadoDiaMenu
          x={estadoDiaMenu.x}
          y={estadoDiaMenu.y}
          lang={lang}
          onTodos={() => marcarTodos(estadoDiaMenu.sesion)}
          onFestivo={() => handleQuitarSesion(estadoDiaMenu.sesion.id)}
          onSuspendido={() => handleQuitarSesion(estadoDiaMenu.sesion.id)}
          onClose={() => setEstadoDiaMenu(null)}
        />
      )}

      {notaCelda && (
        <NotaOverlay
          jugadorNombre={notaCelda.miembro.persona.nombre}
          fecha={fechaCorta(notaCelda.sesion.fecha)}
          lang={lang}
          notaInicial={notaCelda.miembro.registros.find((r) => r.sesion_id === notaCelda.sesion.id)?.nota ?? ''}
          onSave={handleSaveNota}
          onDelete={handleDeleteNota}
          onClose={() => setNotaCelda(null)}
          esAdmin={esAdmin}
          onDeleteSesion={handleDeleteRegistro}
          estadoActual={notaCelda.miembro.registros.find((r) => r.sesion_id === notaCelda.sesion.id)?.estado ?? null}
          onMarcarFalta={readOnly ? undefined : handleMarcarFalta}
        />
      )}


      {fichaMiembroId && (
        <FichaOverlay
          equipoId={equipoId}
          miembroId={fichaMiembroId}
          lang={lang}
          onClose={() => setFichaMiembroId(null)}
          onChanged={refresh}
        />
      )}
    </div>
  )
}
